// What will ACTUALLY happen to each platform on a scheduled post — the honest
// answer the old Scheduled panel didn't give. A platform tagged on a post does
// not mean it will publish: it may be disconnected, not configured on the
// server, or (TikTok today) connected but not yet cleared to post publicly.
//
// This is the single source of truth for that judgement so the banner and the
// panel can never disagree.

import type { ConnectedAccount, DispatchResult, PlatformId, PlatformInfo } from "../api";
import { isPendingPlatformError } from "./errorMessage";

/**
 * Platforms that are connected and configured but still cannot publish, and why.
 *
 * Empty as of 2026-09-03: TikTok's Content Posting API (Direct Post) audit was
 * approved, so TikTok now publishes like any other connected platform. It was
 * gated here from 2026-08-25 to 2026-09-03 while that audit was pending — going
 * Live in production had turned on TikTok's rule that an unaudited client can
 * only post to private accounts. Add an entry back here if a platform ever needs
 * to be held again pending an approval.
 */
const PENDING_APPROVAL: Partial<Record<PlatformId, string>> = {};

/** Why a published-post leg is "held" (not sent) rather than failed — the
 * platform-specific reason where we have one (e.g. TikTok awaiting approval),
 * else a generic line. Used on delivery receipts so a held leg explains itself. */
export function heldReason(id: PlatformId): string {
  return PENDING_APPROVAL[id] ?? "Not sent yet — this platform isn't publishing right now.";
}

export type PublishOutcome = "will_publish" | "pending" | "disconnected" | "unconfigured";

export interface PlatformStatus {
  id: PlatformId;
  name: string;
  outcome: PublishOutcome;
  /** Human-readable reason for anything that is NOT "will_publish". */
  note?: string;
}

/** True only for the outcome that actually reaches the platform. */
export function willPublish(s: PlatformStatus): boolean {
  return s.outcome === "will_publish";
}

/**
 * Resolve, for one platform id on a scheduled post, what will happen to it —
 * using only signals the client actually has: whether the server configured it,
 * whether the user connected an account, and the pending-approval list above.
 */
export function platformStatus(
  id: PlatformId,
  platforms: PlatformInfo[],
  accounts: ConnectedAccount[],
): PlatformStatus {
  const info = platforms.find((p) => p.id === id);
  const name = info?.name ?? id;
  const account = accounts.find((a) => a.platform === id);
  const connected = Boolean(account);

  if (info && !info.configured) {
    return { id, name, outcome: "unconfigured", note: `${name} isn't set up yet` };
  }
  if (!connected) {
    return { id, name, outcome: "disconnected", note: `${name} isn't connected` };
  }
  // Facebook connects the user first, then needs a specific Page chosen before it
  // can publish. The server stores an empty accountId until one is picked, so a
  // connected-but-no-Page account is NOT publish-ready — surface it as a pending
  // "Select a Page" action rather than a false green that only fails at publish.
  if (account && !account.accountId) {
    const note = id === "facebook" ? "Select a Facebook Page" : `Finish connecting ${name}`;
    return { id, name, outcome: "pending", note };
  }
  // A connected-but-stale account (revoked/expired token) will NOT publish until
  // it is re-authorised — treat it as pending with a "Reconnect" instruction so
  // the banner/calendar never promise a post that would silently fail.
  if (account?.needsReconnect) {
    return { id, name, outcome: "pending", note: `Reconnect ${name}` };
  }
  const pending = PENDING_APPROVAL[id];
  if (pending) {
    return { id, name, outcome: "pending", note: pending };
  }
  return { id, name, outcome: "will_publish" };
}

/** Every platform on a post, resolved and ordered will-publish first. */
export function statusesFor(
  targets: PlatformId[],
  platforms: PlatformInfo[],
  accounts: ConnectedAccount[],
): PlatformStatus[] {
  const order: Record<PublishOutcome, number> = {
    will_publish: 0,
    pending: 1,
    disconnected: 2,
    unconfigured: 3,
  };
  return targets
    .map((id) => platformStatus(id, platforms, accounts))
    .sort((a, b) => order[a.outcome] - order[b.outcome]);
}

// ---------------------------------------------------------------------------
// PUBLISHED outcomes — the ACTUAL result of a post that already fired.
// ---------------------------------------------------------------------------
//
// This is deliberately NOT `statusesFor`. `statusesFor` PREDICTS what will
// happen to a scheduled post from client-side signals (connected? configured?
// pending audit?). A published post is history: we know the real answer from
// the DispatchResult the server returned — green if `ok`, red if not — so we
// read it straight off `results` instead of re-deriving a guess.

export type PublishedResultOutcome = "published" | "held" | "failed";

export interface PublishedStatus {
  id: PlatformId;
  name: string;
  /**
   * "published" → the post reached the platform; "held" → it didn't publish for
   * an expected, no-action-needed reason (a platform still pending approval on
   * our side); "failed" → a real error the user may need to act on.
   */
  outcome: PublishedResultOutcome;
  /** Live link to the real post, when the platform returned one. */
  url?: string;
  /** Error text for a failed leg, straight from the dispatch result. */
  error?: string;
}

/**
 * Resolve the ACTUAL per-platform outcome of a published post from its dispatch
 * results. Published legs are ordered first, failed legs after, so a clean post
 * reads as calm/done and any failure is still plainly visible at the end.
 */
export function publishedStatuses(
  results: DispatchResult[],
  platforms: PlatformInfo[],
): PublishedStatus[] {
  const order: Record<PublishedResultOutcome, number> = {
    published: 0,
    held: 1,
    failed: 2,
  };
  return results
    .map((r): PublishedStatus => {
      const info = platforms.find((p) => p.id === r.platform);
      const outcome: PublishedResultOutcome = r.ok
        ? "published"
        : isPendingPlatformError(r.error)
          ? "held"
          : "failed";
      return {
        id: r.platform,
        name: info?.name ?? r.platform,
        outcome,
        url: r.url,
        error: r.error,
      };
    })
    .sort((a, b) => order[a.outcome] - order[b.outcome]);
}
